import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Plus } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  listAdministrativeAreaTypes as listAdministrativeAreaTypesAction,
  listAdministrativeAreaTypesReset as listAdministrativeAreaTypesResetAction,
} from "../../actions";
import { PageBreadcrumb } from "../../components/PageBreadcrumb";
import { WFPButton } from "../../components/ui/button";
import { ALL_ITEMS_PAGE_SIZE } from "../../constants/pagination";
import { getCountryObjectFromCache } from "../../utils";
import { List } from "./components/List";
import { ConnectedAdministrativeAreaTypesEditModal } from "./components/Modal";
import {
  type AdministrativeAreaType,
  type AdministrativeAreaTypesModalOptions,
  type EntityId,
} from "./types";

type AdministrativeAreaTypesListResponse =
  | AdministrativeAreaType[]
  | {
      count?: number;
      results?: AdministrativeAreaType[];
    };

interface AdministrativeAreaTypesListState {
  data: AdministrativeAreaTypesListResponse;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listAdministrativeAreaTypes: AdministrativeAreaTypesListState;
}

interface StateProps {
  administrativeAreaTypes: AdministrativeAreaType[];
  errorMessage: string;
  isFetching: boolean;
}

interface DispatchProps {
  listAdministrativeAreaTypes: () => void;
  listAdministrativeAreaTypesReset: () => void;
}

type WfpManagerAdministrativeAreaTypesViewProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const CLOSED_MODAL_OPTIONS: AdministrativeAreaTypesModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const getListResults = (data: AdministrativeAreaTypesListResponse): AdministrativeAreaType[] => {
  if (Array.isArray(data)) {
    return data;
  }

  return data.results ?? [];
};

const getCachedCountryId = () => {
  const country = getCountryObjectFromCache() as { id?: EntityId | null } | null | undefined;

  return country?.id;
};

const belongsToCurrentCountry = (areaType: AdministrativeAreaType, countryId?: EntityId | null) => {
  if (countryId === undefined || countryId === null) {
    return true;
  }

  return String(areaType.country) === String(countryId);
};

export const WfpManagerAdministrativeAreaTypesView = ({
  isFetching,
  errorMessage,
  administrativeAreaTypes,
  listAdministrativeAreaTypes,
  listAdministrativeAreaTypesReset,
}: WfpManagerAdministrativeAreaTypesViewProps) => {
  const { t } = useTranslation();
  const [modalOptions, setModalOptions] =
    useState<AdministrativeAreaTypesModalOptions>(CLOSED_MODAL_OPTIONS);

  useEffect(() => {
    listAdministrativeAreaTypes();

    return () => {
      listAdministrativeAreaTypesReset();
    };
  }, [listAdministrativeAreaTypes, listAdministrativeAreaTypesReset]);

  const onClickAdd = () => {
    setModalOptions({ id: "add", isOpen: true, type: "create" });
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("header.configuration"), href: "/wfpManager/configuration" },
            { label: t("configuration.item.administrativeAreaTypes") },
          ]}
        />
        <div className="flex items-center justify-between gap-4">
          <h1 className="font-semibold text-2xl">
            {t("configuration.item.administrativeAreaTypes")}
          </h1>
          <WFPButton type="button" onClick={onClickAdd}>
            <Plus className="size-4" aria-hidden="true" />
            {`${t("common.add")} ${t("configuration.item.administrativeAreaTypes")}`}
          </WFPButton>
        </div>
      </div>
      <List
        administrativeAreaTypes={administrativeAreaTypes}
        errorMessage={errorMessage}
        isFetching={isFetching}
        setModalOptions={setModalOptions}
      />
      <ConnectedAdministrativeAreaTypesEditModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
      />
    </main>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  const countryId = getCachedCountryId();

  const administrativeAreaTypes = getListResults(state.listAdministrativeAreaTypes.data).filter(
    areaType => belongsToCurrentCountry(areaType, countryId)
  );

  return {
    administrativeAreaTypes,
    isFetching: state.listAdministrativeAreaTypes.isFetching,
    errorMessage: state.listAdministrativeAreaTypes.errorMessage,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listAdministrativeAreaTypes: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreaTypesAction({ page_size: ALL_ITEMS_PAGE_SIZE })
      ),
    listAdministrativeAreaTypesReset: () =>
      dispatchAction(dispatch, listAdministrativeAreaTypesResetAction()),
  };
};

export const ConnectedWfpManagerAdministrativeAreaTypesView = connect(
  mapStateToProps,
  mapDispatchToProps
)(WfpManagerAdministrativeAreaTypesView);
